// This is a copy of expression-utils.ts in the api project
// With the following changes:
// - remove the callAsyncFunction parameter to make expandExpressions synchronous

import { get as getFromObject, isEqual } from 'lodash';

/**
 * Options for constructing an ExpressionEvaluationError.
 * Includes the cause property for error chaining (ES2022 standard).
 */
interface ExpressionEvaluationErrorOptions {
  cause?: unknown;
}

/**
 * ExpressionEvaluationError is thrown when an expression fails to evaluate.
 * It includes the key path (dot notation) of the field that failed.
 * The original error can be accessed via the 'cause' property.
 */
export class ExpressionEvaluationError extends Error {
  public readonly cause?: unknown;

  constructor(
    message: string,
    public readonly key: string,
    options?: ExpressionEvaluationErrorOptions,
  ) {
    super(message);
    this.name = 'ExpressionEvaluationError';
    this.cause = options?.cause;

    // Capture stack trace properly
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ExpressionEvaluationError);
    }
  }
}

export type ExpressionOnError = (message: string, error?: unknown) => void;

function evalAfter(
  expression: string,
  payload: Record<string, unknown>,
): string {
  if (typeof payload !== 'object' || payload === null) {
    throw new Error(
      `Invalid payload for 'after' function in expression='${expression}'`,
    );
  }
  const secs = payload['secs'] ?? payload['seconds'] ?? payload['sec'];
  if (typeof secs !== 'number') {
    throw new Error(
      `Invalid seconds value for 'after' function in expression='${expression}'`,
    );
  }
  // Return the current time plus the specified seconds
  return new Date(Date.now() + secs * 1000).toISOString();
}

function evalModuloSelect(
  expression: string,
  payload: Record<string, unknown>,
): unknown {
  if (typeof payload !== 'object' || payload === null) {
    throw new Error(
      `Invalid payload for 'moduloSelect' function in expression='${expression}'`,
    );
  }

  const itemsRaw = payload['items'];
  const valueRaw = payload['value'];

  if (!Array.isArray(itemsRaw)) {
    throw new Error(
      `Invalid 'items' for 'moduloSelect' - expected an array in expression='${expression}'`,
    );
  }
  if (typeof valueRaw !== 'number' || !Number.isInteger(valueRaw)) {
    throw new Error(
      `Invalid 'value' for 'moduloSelect' - expected an integer in expression='${expression}'`,
    );
  }

  const items = itemsRaw as unknown[];
  const value = valueRaw as number;

  if (!Array.isArray(items) || items.length === 0) {
    throw new Error(
      `Invalid 'items' array for 'moduloSelect' - must be a non-empty array.`,
    );
  }
  if (typeof value !== 'number' || !Number.isInteger(value)) {
    throw new Error(`Invalid 'value' for 'moduloSelect' - must be an integer.`);
  }

  const idx = ((value % items.length) + items.length) % items.length;
  return items[idx];
}

function evalGetFromJson(
  expression: string,
  payload: Record<string, unknown>,
): unknown {
  if (typeof payload !== 'object' || payload === null) {
    throw new Error(
      `Invalid payload for 'getFromJson' function in expression='${expression}'`,
    );
  }

  const source = (payload as Record<string, unknown>)['json'];
  const path = (payload as Record<string, unknown>)['path'];
  const defaultValue = (payload as Record<string, unknown>)['default'];

  if (typeof path !== 'string') {
    throw new Error(
      `Invalid 'path' for 'getFromJson' - expected string in expression='${expression}'`,
    );
  }

  let root: unknown = source;
  if (typeof source === 'string') {
    try {
      root = JSON.parse(source);
    } catch {
      throw new Error(
        `Invalid 'json' string for 'getFromJson' in expression='${expression}'`,
      );
    }
  } else if (typeof source !== 'object' || source === null) {
    throw new Error(
      `Invalid 'json' for 'getFromJson' - expected string or object in expression='${expression}'`,
    );
  }

  const value = getFromObject(root, path);
  return value === undefined ? defaultValue : value;
}

function evalIf(expression: string, payload: Record<string, unknown>): unknown {
  if (typeof payload !== 'object' || payload === null) {
    throw new Error(
      `Invalid payload for 'if' function in expression='${expression}'`,
    );
  }

  if (!('condition' in payload)) {
    throw new Error(
      `Missing 'condition' for 'if' function in expression='${expression}'`,
    );
  }

  if (!('then' in payload) && !('else' in payload)) {
    throw new Error(
      `Missing both 'then' and 'else' for 'if' function in expression='${expression}'`,
    );
  }

  return payload['condition'] ? payload['then'] : payload['else'];
}

function evalIsEqual(
  expression: string,
  payload: Record<string, unknown>,
): boolean {
  if (typeof payload !== 'object' || payload === null) {
    throw new Error(
      `Invalid payload for 'isEqual' function in expression='${expression}'`,
    );
  }

  if (!('value' in payload) || !('other' in payload)) {
    throw new Error(
      `Missing 'value' or 'other' for 'isEqual' function in expression='${expression}'`,
    );
  }

  return isEqual(payload['value'], payload['other']);
}

function evalFirstNonEmpty(
  expression: string,
  payload: Record<string, unknown>,
): unknown {
  if (typeof payload !== 'object' || payload === null) {
    throw new Error(
      `Invalid payload for 'firstNonEmpty' function in expression='${expression}'`,
    );
  }

  const valuesRaw = payload['values'];
  if (!Array.isArray(valuesRaw)) {
    throw new Error(
      `Invalid 'values' for 'firstNonEmpty' - expected an array in expression='${expression}'`,
    );
  }

  for (const value of valuesRaw as unknown[]) {
    if (value !== null && value !== undefined && value !== '') {
      return value;
    }
  }

  return undefined;
}

type ObjectExpressionProperty = {
  key: string;
  valueExpression: string;
};

function skipWhitespace(source: string, startIndex: number): number {
  let nextIndex = startIndex;
  while (nextIndex < source.length && /\s/.test(source[nextIndex])) {
    nextIndex += 1;
  }
  return nextIndex;
}

function parsePropertyKeyLiteral(
  source: string,
  startIndex: number,
): { value: string; nextIndex: number } {
  if (source[startIndex] !== '"') {
    throw new Error('Invalid string literal');
  }

  let index = startIndex + 1;
  let escaped = false;

  while (index < source.length) {
    const char = source[index];
    if (escaped) {
      escaped = false;
    } else if (char === '\\') {
      escaped = true;
    } else if (char === '"') {
      const literal = source.slice(startIndex, index + 1);
      return {
        value: JSON.parse(literal),
        nextIndex: index + 1,
      };
    }
    index += 1;
  }

  throw new Error('Unterminated string literal');
}

/**
 * Parses the value expression for a property in an object expression.
 *
 * This function starts at the given index of the source string, skips any initial whitespace,
 * and then iterates through the string to find the end of the value expression for a specific property.
 * It handles nested structures such as brackets {}, [], and (), as well as string literals,
 * to ensure that commas inside those structures do not prematurely terminate the value.
 * The value is considered to end when a comma is encountered at the top-level (i.e., not inside
 * any nested brackets, parens, or string literals), or at the end of the payload.
 *
 * Returns the substring containing the raw value and the next index to continue parsing from.
 *
 * Example:
 *   Given source: '"items": ["a", "b", "c"], "value": 3', startIndex at first '[', will extract
 *   '["a", "b", "c"]' as the value for 'items'.
 */
function parsePropertyValueExpression(
  source: string,
  startIndex: number,
  expression: string,
  key: string,
): { rawValue: string; nextIndex: number } {
  let index = skipWhitespace(source, startIndex);
  const valueStart = index;
  let inString = false;
  let stringQuote: '"' | "'" | null = null;
  let depthCurly = 0;
  let depthBracket = 0;
  let depthParen = 0;

  while (index < source.length) {
    const char = source[index];

    if (inString) {
      if (char === '\\') {
        index += 2;
        continue;
      }
      if (char === stringQuote) {
        inString = false;
      }
      index += 1;
      continue;
    }

    if (char === '"' || char === "'") {
      inString = true;
      stringQuote = char as '"' | "'";
      index += 1;
      continue;
    }

    switch (char) {
      case '{':
        depthCurly += 1;
        break;
      case '}':
        if (depthCurly > 0) {
          depthCurly -= 1;
        }
        break;
      case '[':
        depthBracket += 1;
        break;
      case ']':
        if (depthBracket > 0) {
          depthBracket -= 1;
        }
        break;
      case '(':
        depthParen += 1;
        break;
      case ')':
        if (depthParen > 0) {
          depthParen -= 1;
        }
        break;
      case ',':
        if (depthCurly === 0 && depthBracket === 0 && depthParen === 0) {
          const rawValue = source.slice(valueStart, index).trim();
          if (!rawValue.length) {
            throw new Error(
              `Empty value for key '${key}' in expression='${expression}'`,
            );
          }
          return { rawValue, nextIndex: index };
        }
        break;
    }

    index += 1;
  }

  const rawValue = source.slice(valueStart, index).trim();
  if (!rawValue.length) {
    throw new Error(
      `Empty value for key '${key}' in expression='${expression}'`,
    );
  }
  return { rawValue, nextIndex: index };
}

function parseObjectProperties(
  expression: string,
  rawPayload: string,
): ObjectExpressionProperty[] {
  const trimmed = rawPayload.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) {
    throw new Error('Payload must be a JSON object');
  }

  const inner = trimmed.slice(1, -1);
  const entries: ObjectExpressionProperty[] = [];
  let index = 0;

  while (index < inner.length) {
    index = skipWhitespace(inner, index);
    if (index >= inner.length) {
      break;
    }

    if (inner[index] !== '"') {
      throw new Error('Payload keys must be strings');
    }

    const { value: key, nextIndex } = parsePropertyKeyLiteral(inner, index);
    index = skipWhitespace(inner, nextIndex);
    if (inner[index] !== ':') {
      throw new Error(`Missing ':' after key '${key}'`);
    }
    index += 1;

    const { rawValue, nextIndex: afterValue } = parsePropertyValueExpression(
      inner,
      index,
      expression,
      key,
    );
    entries.push({ key, valueExpression: rawValue });
    index = skipWhitespace(inner, afterValue);

    if (index >= inner.length) {
      break;
    }

    if (inner[index] === ',') {
      index += 1;
    } else {
      throw new Error(`Invalid delimiter after key '${key}'`);
    }
  }

  return entries;
}

function evalObjectWithExpressions(
  expression: string,
  rawPayload: string,
  context: Record<string, unknown>,
  onError: ExpressionOnError | undefined,
): Record<string, unknown> {
  let entries: ObjectExpressionProperty[];
  try {
    entries = parseObjectProperties(expression, rawPayload);
  } catch (error) {
    onError?.(
      `Failed to parse JSON payload from expression='${expression}'`,
      error,
    );
    throw new Error(`Invalid JSON payload in expression='${expression}'`);
  }

  const resolvedPayload: Record<string, unknown> = {};

  for (const { key, valueExpression: rawValue } of entries) {
    const trimmedValue = rawValue.trim();
    if (!trimmedValue.length) {
      throw new Error(
        `Invalid payload value for key '${key}' in expression='${expression}'`,
      );
    }

    let isJsonLiteral = false;
    try {
      resolvedPayload[key] = JSON.parse(trimmedValue) as unknown;
      isJsonLiteral = true;
    } catch {
      // Not a JSON literal, fall through to other parsing.
    }

    if (!isJsonLiteral) {
      resolvedPayload[key] = evalExpression(trimmedValue, context, onError);
    }
  }

  return resolvedPayload;
}

function parseArrayElements(expression: string, rawPayload: string): string[] {
  const trimmed = rawPayload.trim();
  if (!trimmed.startsWith('[') || !trimmed.endsWith(']')) {
    throw new Error('Payload must be a JSON array');
  }

  const inner = trimmed.slice(1, -1);
  const elements: string[] = [];
  let index = 0;
  let elementIndex = 0;

  while (index < inner.length) {
    index = skipWhitespace(inner, index);
    if (index >= inner.length) {
      break;
    }

    const { rawValue, nextIndex: afterValue } = parsePropertyValueExpression(
      inner,
      index,
      expression,
      `[${elementIndex}]`,
    );
    elements.push(rawValue);
    index = skipWhitespace(inner, afterValue);

    if (index >= inner.length) {
      break;
    }

    if (inner[index] === ',') {
      index += 1;
      elementIndex += 1;
    } else {
      throw new Error(`Invalid delimiter after element ${elementIndex}`);
    }
  }

  return elements;
}

function evalArrayExpression(
  expression: string,
  rawPayload: string,
  context: Record<string, unknown>,
  onError: ExpressionOnError | undefined,
): unknown[] {
  const elements = parseArrayElements(expression, rawPayload);
  const resolved: unknown[] = [];

  for (const rawElement of elements) {
    const trimmedElement = rawElement.trim();
    if (!trimmedElement.length) {
      throw new Error(`Invalid array element in expression='${expression}'`);
    }

    let isJsonLiteral = false;
    try {
      resolved.push(JSON.parse(trimmedElement) as unknown);
      isJsonLiteral = true;
    } catch {
      // Not a JSON literal, fall through to expression evaluation.
    }

    if (!isJsonLiteral) {
      resolved.push(evalExpression(trimmedElement, context, onError));
    }
  }

  return resolved;
}

function evalFunctionExpression(
  expression: string,
  context: Record<string, unknown>,
  onError: ExpressionOnError | undefined,
) {
  // Expected expression format: {functionName}(jsonPayloadStr)
  const match = expression.match(/([a-zA-Z0-9_]+)\((.+)\)/);
  if (!match) {
    throw new Error(`Invalid expression format expression='${expression}'`);
  }

  const [, functionName, jsonPayloadStr] = match;

  const jsonPayload = evalObjectWithExpressions(
    expression,
    jsonPayloadStr,
    context,
    onError,
  );

  // Handle different function types
  switch (functionName) {
    case 'after':
      return evalAfter(expression, jsonPayload);
    case 'moduloSelect':
      return evalModuloSelect(expression, jsonPayload);
    case 'getFromJson':
      return evalGetFromJson(expression, jsonPayload);
    case 'if':
      return evalIf(expression, jsonPayload);
    case 'isEqual':
      return evalIsEqual(expression, jsonPayload);
    case 'firstNonEmpty':
      return evalFirstNonEmpty(expression, jsonPayload);
    case 'callAsyncFunction': {
      throw new Error(
        `'callAsyncFunction' is not available in synchronous expression evaluation='${expression}'`,
      );
    }
    default:
      throw new Error(
        `Invalid functionName '${functionName}' in expression='${expression}'`,
      );
  }
}

function evalExpression(
  expression: string,
  context: Record<string, unknown>,
  onError: ExpressionOnError | undefined,
): string | Record<string, unknown> | unknown {
  const contextExpressionRoots = Object.keys(context);

  // First check if this is an array expression
  if (expression.startsWith('[') && expression.endsWith(']')) {
    return evalArrayExpression(expression, expression, context, onError);
  }

  // Next check if this is an object expression
  if (expression.startsWith('{') && expression.endsWith('}')) {
    return evalObjectWithExpressions(expression, expression, context, onError);
  }

  // Next check if this is a context expression
  for (const contextExpressionRoot of contextExpressionRoots) {
    const contextExpressionPrefix = `${contextExpressionRoot}.`;
    if (expression.startsWith(contextExpressionPrefix)) {
      const contextPath = expression.substring(contextExpressionPrefix.length);
      return getFromObject(context[contextExpressionRoot], contextPath);
    }
  }

  // Finally, check if this is a function expression
  return evalFunctionExpression(expression, context, onError);
}

type ExpressionScanResult = {
  beforeText: string;
  expressionText: string | null;
  rawExpressionWithDelimiters: string;
  nextIndex: number;
  unterminated: boolean;
};

function formatEmbeddedExpressionEvaluation(expressionResult: unknown): string {
  if (expressionResult !== null && typeof expressionResult === 'object') {
    return JSON.stringify(expressionResult);
  }
  return String(expressionResult ?? '');
}

function findExpressionSegment(
  value: string,
  startIndex: number,
): ExpressionScanResult | null {
  const openIndex = value.indexOf('{{', startIndex);
  if (openIndex === -1) {
    return null;
  }

  const beforeText = value.slice(startIndex, openIndex);
  let cursor = openIndex + 2;
  let inString = false;
  let stringQuote: '"' | "'" | null = null;
  let escaped = false;
  let depthCurly = 0;
  let closeIndex = -1;

  while (cursor < value.length) {
    const char = value[cursor];

    if (escaped) {
      escaped = false;
      cursor += 1;
      continue;
    }

    if (inString) {
      if (char === '\\') {
        escaped = true;
      } else if (char === stringQuote) {
        inString = false;
        stringQuote = null;
      }
      cursor += 1;
      continue;
    }

    if (char === '"' || char === "'") {
      inString = true;
      stringQuote = char as '"' | "'";
      cursor += 1;
      continue;
    }

    if (char === '{') {
      depthCurly += 1;
      cursor += 1;
      continue;
    }

    if (char === '}') {
      if (depthCurly > 0) {
        depthCurly -= 1;
        cursor += 1;
        continue;
      }
      if (cursor + 1 < value.length && value[cursor + 1] === '}') {
        closeIndex = cursor;
        break;
      }
    }

    cursor += 1;
  }

  if (closeIndex === -1) {
    return {
      beforeText,
      expressionText: null,
      rawExpressionWithDelimiters: value.slice(openIndex),
      nextIndex: value.length,
      unterminated: true,
    };
  }

  return {
    beforeText,
    expressionText: value.slice(openIndex + 2, closeIndex).trim(),
    rawExpressionWithDelimiters: value.slice(openIndex, closeIndex + 2),
    nextIndex: closeIndex + 2,
    unterminated: false,
  };
}

function expandExpressionsInString(
  value: string,
  context: Record<string, unknown>,
  onError: ExpressionOnError | undefined,
): string | Record<string, unknown> | unknown {
  const trimmedValue = value.trim();

  // Special case handling for strings only with a single expression delimiter. In this case,
  // we want to allow for types that are non-string, like numbers.
  if (trimmedValue.startsWith('{{') && trimmedValue.endsWith('}}')) {
    const expression = trimmedValue
      .substring(2, trimmedValue.length - 2)
      .trim();
    return evalExpression(expression, context, onError);
  }

  let resultStr = '';
  let currentIndex = 0;
  let foundExpression = false;

  while (currentIndex < value.length) {
    const segment = findExpressionSegment(value, currentIndex);
    if (!segment) {
      resultStr += value.slice(currentIndex);
      break;
    }

    foundExpression = true;
    resultStr += segment.beforeText;

    if (segment.unterminated) {
      resultStr += segment.rawExpressionWithDelimiters;
      break;
    }

    try {
      const expressionResult = evalExpression(
        segment.expressionText ?? '',
        context,
        onError,
      );
      resultStr += formatEmbeddedExpressionEvaluation(expressionResult);
    } catch (error) {
      onError?.(
        `Failed to evaluate embedded expression '${segment.rawExpressionWithDelimiters}'`,
        error,
      );
      resultStr += segment.rawExpressionWithDelimiters;
    }

    currentIndex = segment.nextIndex;
  }

  if (!foundExpression) {
    return value;
  }

  return resultStr;
}

/**
 * Wraps an error in ExpressionEvaluationError if it isn't already one.
 * Preserves the original error as the cause for debugging.
 */
function wrapExpressionError(
  error: unknown,
  keyPath: string,
): ExpressionEvaluationError {
  if (error instanceof ExpressionEvaluationError) {
    return error;
  }
  const message = error instanceof Error ? error.message : String(error);
  return new ExpressionEvaluationError(message, keyPath, { cause: error });
}

/**
 * Internal helper for expandExpressions that tracks the dot-path key for error reporting.
 */
function expandExpressionsInternal(
  unexpandedRecord: Record<string, unknown>,
  context: Record<string, unknown>,
  onError: ExpressionOnError | undefined,
  parentPath: string,
): Record<string, unknown> {
  const expandedRecord: Record<string, unknown> = {};

  for (const [key, origValue] of Object.entries(unexpandedRecord)) {
    const currentKeyPath = parentPath ? `${parentPath}.${key}` : key;

    if (typeof origValue === 'string') {
      try {
        expandedRecord[key] = expandExpressionsInString(
          origValue,
          context,
          onError,
        );
      } catch (error) {
        throw wrapExpressionError(error, currentKeyPath);
      }
    } else if (Array.isArray(origValue)) {
      // Recursively expand array elements
      expandedRecord[key] = origValue.map((item, index) => {
        const arrayItemPath = `${currentKeyPath}[${index}]`;
        if (typeof item === 'string') {
          try {
            return expandExpressionsInString(item, context, onError);
          } catch (error) {
            throw wrapExpressionError(error, arrayItemPath);
          }
        } else if (typeof item === 'object' && item !== null) {
          try {
            return expandExpressionsInternal(
              item as Record<string, unknown>,
              context,
              onError,
              arrayItemPath,
            );
          } catch (error) {
            throw wrapExpressionError(error, arrayItemPath);
          }
        }
        return item;
      });
    } else if (typeof origValue === 'object' && origValue !== null) {
      // Recursively expand nested objects
      try {
        expandedRecord[key] = expandExpressionsInternal(
          origValue as Record<string, unknown>,
          context,
          onError,
          currentKeyPath,
        );
      } catch (error) {
        throw wrapExpressionError(error, currentKeyPath);
      }
    } else {
      expandedRecord[key] = origValue;
    }
  }

  return expandedRecord;
}

/**
 * Recursively walks a JSON-like record and expands any `{{ ... }}` expressions found in
 * string values into their evaluated forms.
 *
 * Phase 1 - Structural Traversal:
 * - Starts from `unexpandedRecord` and traverses all nested objects and arrays.
 * - Whenever it encounters a string, it scans for one or more `{{ ... }}` segments and
 *   replaces each segment with the result of evaluating the enclosed expression.
 * - Non-string primitives (numbers, booleans, null, etc.) are left unchanged.
 *
 * Phase 2 - Expression Evaluation Inside `{{ ... }}`:
 * - Expressions are the content between `{{` and `}}`.
 * - Supported expression kinds:
 *   - **Context lookups**: `rootKey.path.to.value` where `rootKey` is a key in `context`
 *     (for example `workflow.context.from_numbers[0].id`); the dotted path is resolved
 *     against the corresponding value in the `context` object.
 *   - **Object expressions**: an expression that starts with `{` and ends with `}` is
 *     treated as a JSON object whose property values may themselves be JSON literals or
 *     further expressions; this produces a structured `Record<string, unknown>`.
 *   - **Function expressions**: `functionName({ ... })` where the payload object may
 *     contain nested expressions in its values; the function name identifies which
 *     built-in behavior to apply.
 *
 * Built-in functions available in expressions:
 * - `after({ "secs" | "seconds" | "sec": number })` → returns an ISO-8601 timestamp for
 *   the current time plus the given number of seconds.
 * - `moduloSelect({ "items": any[], "value": integer })` → deterministically selects one
 *   element from `items` based on `value % items.length`.
 * - `getFromJson({ "json": string | object, "path": string, "default"?: any })` →
 *   reads a dotted path from a JSON string or object, returning `default` when the path
 *   does not resolve to a value.
 * - `if({ "condition": any, "then": any, "else"?: any })` → returns `then` when
 *   `condition` is truthy; otherwise returns `else` (which may be undefined if omitted).
 * - `isEqual({ "value": any, "other": any })` → returns `true` when `value` is deeply
 *   equal to `other`; otherwise returns `false`.
 * - `firstNonEmpty({ "values": any[] })` → returns the first element from `values` that
 *   is not `null`, `undefined`, or the empty string `""`; returns `undefined` when no
 *   candidate qualifies.
 * - `callAsyncFunction({ "name": string, "args": object })` → delegates to the optional
 *   async hook passed into `expandExpressions`, allowing callers to plug in custom async
 *   function evaluation (such as `fluents.expandTags`) during expansion.
 */
export function expandExpressions(
  unexpandedRecord: Record<string, unknown>,
  context: Record<string, unknown>,
  onError?: ExpressionOnError,
): Record<string, unknown> {
  try {
    return expandExpressionsInternal(unexpandedRecord, context, onError, '');
  } catch (error) {
    // Ensure callers only ever see ExpressionEvaluationError from this API.
    throw wrapExpressionError(error, '');
  }
}
